import { useContext, useEffect, useState } from 'react';

import {
	ArrowLeftOutlined,
	DeleteOutlined,
	EditOutlined,
	SearchOutlined,
} from '@ant-design/icons';
import {
	Button,
	Flex,
	Input,
	Popconfirm,
	Tooltip,
	Typography,
	message,
} from 'antd';
import { cp } from 'fs';
import { useNavigate, useParams } from 'react-router-dom';

import {
	useDeleteSingleCollection,
	useGetSingleCreatorsCollectionById,
} from '@/api/monitor_collections';
import EditCollectionModal from '@/components/modals/EditCollectionModal';
import EditCreatorModal from '@/components/modals/EditCreatorModal';
import { useDebounce } from '@/hooks';
import { CollectionContext } from '@/providers/CollectionsContextProvider';
import { IPagination } from '@/ts';

import AddCreatorModal from '../../../../../../components/modals/AddCreatorModal';
import SingleCollectionCreatorsTable from './SingleCollectionCreatorsTable';
import { singleCollectionCreatorsColumns } from './data';

export default function CreatorsGroupCollection() {
	const params = useParams();
	const navigate = useNavigate();
	const { collection, setCollection, modalData, setModalData, data } =
		useContext(CollectionContext);

	const [isCreatorModalOpen, setIsCreatorModalOpen] = useState(false);
	const [isEditCreatorModalOpen, setIsEditCreatorModalOpen] = useState(false);
	const [isEditCollectionModalOpen, setIsEditCollectionModalOpen] =
		useState(false);

	// const [sortBy, setSortBy] = useState<"created_at" | "status">("created_at");
	const [search, setSearch] = useState('');
	const [sort, setSort] = useState<'requested_at' | 'status'>('requested_at');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

	const [pagination, setPagination] = useState<IPagination>({
		page: Number(localStorage.getItem('page') ?? 1),
		pageCount: 1,
		total: 0,
		limit: Number(localStorage.getItem('limit') ?? 10),
	});

	const debouncedSearch = useDebounce(search);

	const {
		data: singleCreatorsData,
		isLoading,
		isFetching,
		isError: isCollectionDataError,
		error: collectionDataError,
	} = useGetSingleCreatorsCollectionById({
		id: params?.id,
		pagination,
		sort,
		sortOrder,
		enabled: collection === 'creators-group' && !!params?.id,
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
	});

	const { mutate: deleteCollection, isLoading: isDeleteLoading } =
		useDeleteSingleCollection('creators');

	useEffect(() => {
		if (
			isCollectionDataError &&
			(collectionDataError as any)?.response?.status === 404
		) {
			navigate(
				`/app/dashboard/video-insights-co-pilot/collections/creators/${params?.id}/_`,
			);
		} else if (isCollectionDataError && collectionDataError) {
			message.error('An error occurred while fetching the collection data.');
		}
	}, [isCollectionDataError, collectionDataError]);

	const item = singleCreatorsData?.data?.data?.item || {};
	const items = singleCreatorsData?.data?.data?.items || [];
	const total = singleCreatorsData?.data?.data?.pagination?.total || 0;

	useEffect(() => {
		return () => {
			setSearch('');
		};
	}, []);

	const onTableChange = (__: any, _: any, sorter: any) => {
		setSort(sorter.field ?? 'requested_at');

		setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
	};

	const onEditCreatorClick = (record: any) => {
		setModalData({
			...record,
			collection_id: params?.id,
		});
		setIsEditCreatorModalOpen(true);
	};

	const onDeleteClick = (id: number) => {
		deleteCollection(
			{
				id,
				type: 'creators',
			},
			{
				onSuccess: () => {
					message.success('Removed');
					setSearch('');
					setCollection('creators');
				},
				onError: (error) => {
					const err = error as any;
					console.error(
						'Error deleting collection:',
						err.response.data.message,
					);
					message.error(err.response.data.message);
				},
			},
		);
	};

	if (collection !== 'creators-group') {
		return null;
	}

	return (
		<>
			<Flex vertical gap={8} align="flex-start" style={{ width: '100%' }}>
				<Flex
					gap={8}
					align="center"
					justify="space-between"
					style={{ width: '100%' }}
				>
					{(item?.title ?? data?.title) && (
						<Flex vertical>
							<Typography.Title
								level={5}
								style={{ textAlign: 'left', margin: '0' }}
							>
								{item?.title ?? data?.title}
							</Typography.Title>

							{(item?.description ?? data?.description) && (
								<Typography.Text style={{ color: 'gray' }}>
									{item?.description ?? data?.description}
								</Typography.Text>
							)}
						</Flex>
					)}
					<Flex gap={8} style={{ marginLeft: 'auto' }}>
						<Tooltip title="Edit">
							<Button
								onClick={() => {
									setIsEditCollectionModalOpen(true);
									setModalData({
										...data,
										title: item?.title,
										description: item?.description,
									});
								}}
								disabled={isDeleteLoading || isFetching || !item?.title}
							>
								<EditOutlined />
							</Button>
						</Tooltip>
						<Popconfirm
							title={`Sure to remove collection?`}
							onConfirm={() => onDeleteClick(Number(params?.id))}
						>
							<Tooltip title="Remove">
								<Button
									style={{ fontSize: '16px' }}
									disabled={isDeleteLoading || isFetching || !item?.title}
								>
									<DeleteOutlined />
								</Button>
							</Tooltip>
						</Popconfirm>
						<Button
							type="primary"
							onClick={() => {
								setIsCreatorModalOpen(true);
								setModalData(data);
							}}
							disabled={isDeleteLoading || isFetching || !item?.title}
						>
							Add creator
						</Button>
						<Tooltip title="Back">
							<Button
								onClick={() => {
									setCollection('creators');
									navigate(
										'/app/dashboard/video-insights-co-pilot/collections/creators',
									);
									setSearch('');
								}}
							>
								<ArrowLeftOutlined />
							</Button>
						</Tooltip>
					</Flex>
				</Flex>
				<Flex>
					<Input
						placeholder="Search..."
						suffix={<SearchOutlined />}
						// style={{ width: "200px", maxWidth: "200px" }}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</Flex>
				<SingleCollectionCreatorsTable
					data={items}
					columns={singleCollectionCreatorsColumns}
					onTableChange={onTableChange}
					isLoading={isLoading || isFetching || isDeleteLoading}
					pagination={{
						...pagination,
						total,
					}}
					setPagination={setPagination}
					onEditCreatorClick={onEditCreatorClick}
				/>
			</Flex>

			{/* end secondary collections */}
			<AddCreatorModal
				isOpen={isCreatorModalOpen}
				setOpen={setIsCreatorModalOpen}
				type="creators"
			/>
			<EditCreatorModal
				isOpen={isEditCreatorModalOpen}
				setOpen={setIsEditCreatorModalOpen}
				data={modalData}
			/>
			<EditCollectionModal
				isOpen={isEditCollectionModalOpen}
				setOpen={setIsEditCollectionModalOpen}
				data={{
					...modalData,
					type: 'creators',
				}}
				setData={setModalData}
			/>
		</>
	);
}
